
import type { PropType } from 'vue'
import type {
  CategoriesDataItem,
  CategoriesMessages,
  CategoriesQueryData,
  Counters,
  DataTableColumn,
} from '../../../models'
import { defineComponent } from 'vue'

import { mapGetters } from 'vuex'

import { AlignEnum, KpiEnum } from '../../../enums'

import { capitalizeSentence } from '../../../filters'

import { CONFIG_CONSTS } from '../../../store/config/config.consts'

import EpbAside from '../../aside.component.vue'
import DataTable from '../../crud/data-table.component.vue'
import GaugeSemicircleChart from '../../gauge-semicircle-chart.component.vue'
import { EpbButton } from '../../shared/epb-button'
import TransitionSize from '../../transition-size.component.vue'

export default defineComponent({
  name: 'CategoriesQueriesAside',
  components: {
    EpbAside,
    DataTable,
    TransitionSize,
    GaugeSemicircleChart,
    EpbButton,
  },
  props: {
    data: {
      type: Object as PropType<CategoriesDataItem>,
      required: true,
    },
    loading: {
      type: Object as PropType<{ table: boolean; downloadButton: boolean }>,
      required: true,
    },
    messages: {
      type: Object as PropType<CategoriesMessages>,
      required: true,
    },
  },
  computed: {
    ...mapGetters(CONFIG_CONSTS.moduleName, {
      kpis: CONFIG_CONSTS.getters.searchKpis,
    }),
    _kpis(): KpiEnum[] {
      return [KpiEnum.QUERIES, KpiEnum.FINDABILITY, KpiEnum.ADD2CART].filter((kpi: KpiEnum) =>
        this.kpis.includes(kpi),
      )
    },
    gaugeKpis(): KpiEnum[] {
      const gaugeKpis = [...this._kpis]
      if (!this.data.queryCountPct && this._kpis.includes(KpiEnum.QUERIES)) {
        const queriesIndex = gaugeKpis.findIndex((kpi: KpiEnum) => kpi === KpiEnum.QUERIES)
        gaugeKpis.splice(queriesIndex, 1)
      }
      return gaugeKpis
    },
    tableKey(): string {
      return this.data.queries ? 'queries' : 'children'
    },
    tableColumns(): DataTableColumn[] {
      let initialColumn: DataTableColumn
      if (this.data.queries) {
        initialColumn = {
          id: 'query',
          text: capitalizeSentence(this.$epb.messages.insights.analytics.queryName),
          tooltip: this.messages.tooltips.queriesTable.query,
        }
      } else {
        initialColumn = {
          id: 'name',
          text: capitalizeSentence(this.messages.categoryLabel),
          tooltip: this.messages.tooltips.categoriesTable.category,
        }
      }
      return [initialColumn, ...this.counterColumns]
    },
    counterColumns(): DataTableColumn[] {
      const tooltipMessages = this.messages.tooltips
      const tableTooltips = this.data.queries
        ? tooltipMessages.queriesTable
        : tooltipMessages.categoriesTable
      return this._kpis.reduce((columns: DataTableColumn[], kpi: KpiEnum) => {
        ;(
          ({
            [KpiEnum.QUERIES]: () =>
              columns.push(
                {
                  id: 'queryCount',
                  text: capitalizeSentence(this.$epb.messages.insights.analytics.volume),
                  tooltip: tooltipMessages.volume,
                  align: AlignEnum.CENTER,
                },
                {
                  id: 'queryCountPct',
                  text: capitalizeSentence(this.messages.percentageLabel),
                  tooltip: tableTooltips.percentage,
                  align: AlignEnum.CENTER,
                },
              ),
            [KpiEnum.FINDABILITY]: () =>
              columns.push({
                id: 'findabilityPct',
                text: capitalizeSentence(this.getKpiLabel(kpi)),
                tooltip: tableTooltips.findability,
                align: AlignEnum.CENTER,
              }),
            [KpiEnum.ADD2CART]: () =>
              columns.push({
                id: 'add2cartCountPct',
                text: capitalizeSentence(this.getKpiLabel(kpi)),
                tooltip: tableTooltips.add2cart,
                align: AlignEnum.CENTER,
              }),
          })[kpi] as () => void
        )()
        return columns
      }, [])
    },
    tableData(): any[] {
      if (this.data.queries) {
        return this.data.queries.map((queryData: CategoriesQueryData) => ({
          query: queryData.query,
          ...this.counterData(queryData),
        }))
      }
      return this.data.children.map((childData: CategoriesDataItem) => ({
        name: childData.name,
        ...this.counterData(childData),
      }))
    },
  },
  data() {
    return {
      baseClass: 'epb-categories-aside',
      percentageKpis: ['queryCountPct', 'findabilityPct', 'add2cartCountPct'],
    }
  },
  methods: {
    getKpiLabel(kpi: KpiEnum): string {
      const kpiMessage = this.$epb.messages.insights.analytics.kpis[kpi]
      const labelFn: Record<string, () => string> = {
        [KpiEnum.QUERIES]: () => kpiMessage.valueLabel,
        default: () => kpiMessage.percentageLabel,
      }
      return (labelFn[kpi] || labelFn.default)()
    },
    getKpiValue(kpi: KpiEnum): number {
      const value = (
        {
          [KpiEnum.QUERIES]: () => this.data.queryCountPct,
          [KpiEnum.FINDABILITY]: () => this.data.findabilityPct,
          [KpiEnum.ADD2CART]: () => this.data.add2cartCountPct,
        }[kpi] as () => number
      )()
      return Math.round(value * 1e2) / 1e2
    },
    counterData(dataItem: CategoriesDataItem | CategoriesQueryData): Counters {
      return {
        queryCount: dataItem.queryCount,
        queryCountPct: dataItem.queryCountPct,
        findabilityPct: dataItem.findabilityPct,
        add2cartCountPct: dataItem.add2cartCountPct,
      }
    },
    getPercentageKpiText(kpiPct: number): string {
      return `${kpiPct.toLocaleString(this.$epb.locale, { maximumFractionDigits: 2 })}%`
    },
  },
})
